import React, { useEffect, useRef, useState } from 'react';
import { api, formatApiError } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { hasPlanFeature } from '../lib/plans';
import { PlanUpgradeNotice } from './PlanUpgradeNotice';
import { Image, Upload, Trash2, Store } from 'lucide-react';

function ImageSlot({ label, hint, value, uploading, disabled, error, onUpload, onRemove, testId, aspect = 'wide' }) {
    const inputRef = useRef(null);
    const isSquare = aspect === 'square';

    return (
        <div data-testid={testId}>
            <label className="label">{label}</label>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.82rem', margin: '0 0 0.6rem', lineHeight: 1.4 }}>{hint}</p>
            {value ? (
                <div style={{ position: 'relative', borderRadius: 12, overflow: 'hidden', marginBottom: 8, border: '1px solid var(--border)' }}>
                    <img
                        src={value}
                        alt=""
                        style={{
                            width: '100%',
                            height: isSquare ? 120 : 160,
                            objectFit: 'cover',
                            display: 'block',
                        }}
                    />
                    <button type="button" className="btn btn-danger btn-sm" onClick={onRemove}
                            style={{ position: 'absolute', top: 8, right: 8 }}>
                        <Trash2 size={13} /> Șterge
                    </button>
                </div>
            ) : (
                <div style={{
                    height: isSquare ? 120 : 160,
                    borderRadius: 12,
                    background: 'var(--surface-2)',
                    border: '1px dashed var(--border-strong)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--text-faint)',
                    marginBottom: 8,
                    fontSize: '0.85rem',
                }}>
                    Nicio imagine încă
                </div>
            )}
            <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }}
                   onChange={e => { const f = e.target.files?.[0]; if (f) onUpload(f); e.target.value = ''; }} />
            <button type="button" className="btn btn-secondary btn-sm" disabled={disabled || uploading}
                    onClick={() => inputRef.current?.click()}>
                {uploading ? <span className="spinner" /> : <Upload size={13} />}
                {value ? ' Înlocuiește' : ' Încarcă'}
            </button>
            {error && <div style={{ color: 'var(--neon-magenta)', fontSize: '0.82rem', marginTop: 6 }}>{error}</div>}
        </div>
    );
}

export function RestaurantBrandingSettings() {
    const { refresh, restaurant } = useAuth();
    const brandingAllowed = hasPlanFeature(restaurant, 'branding');
    const [logoUrl, setLogoUrl] = useState(null);
    const [coverUrl, setCoverUrl] = useState(null);
    const [uploadConfigured, setUploadConfigured] = useState(false);
    const [logoBusy, setLogoBusy] = useState(false);
    const [coverBusy, setCoverBusy] = useState(false);
    const [logoError, setLogoError] = useState('');
    const [coverError, setCoverError] = useState('');
    const [loading, setLoading] = useState(true);

    const load = () => {
        setLoading(true);
        Promise.all([
            api.get('/r/restaurant/settings'),
            api.get('/r/uploads/status'),
        ]).then(([settingsRes, uploadRes]) => {
            const branding = settingsRes.data.branding || {};
            setLogoUrl(branding.logo_url || null);
            setCoverUrl(branding.cover_url || null);
            setUploadConfigured(Boolean(uploadRes.data.configured));
        }).catch(() => {})
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []);

    const uploadLogo = async (file) => {
        setLogoError(''); setLogoBusy(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const { data } = await api.post('/r/restaurant/logo', fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setLogoUrl(data.logo_url);
            await refresh({ silent: true });
        } catch (e) {
            setLogoError(formatApiError(e));
        } finally { setLogoBusy(false); }
    };

    const uploadCover = async (file) => {
        setCoverError(''); setCoverBusy(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const { data } = await api.post('/r/restaurant/cover', fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setCoverUrl(data.cover_url);
            await refresh({ silent: true });
        } catch (e) {
            setCoverError(formatApiError(e));
        } finally { setCoverBusy(false); }
    };

    const removeLogo = async () => {
        if (!window.confirm('Ștergi logo-ul?')) return;
        setLogoError(''); setLogoBusy(true);
        try {
            await api.delete('/r/restaurant/logo');
            setLogoUrl(null);
            await refresh({ silent: true });
        } catch (e) {
            setLogoError(formatApiError(e));
        } finally { setLogoBusy(false); }
    };

    const removeCover = async () => {
        if (!window.confirm('Ștergi imaginea cover?')) return;
        setCoverError(''); setCoverBusy(true);
        try {
            await api.delete('/r/restaurant/cover');
            setCoverUrl(null);
            await refresh({ silent: true });
        } catch (e) {
            setCoverError(formatApiError(e));
        } finally { setCoverBusy(false); }
    };

    if (loading) {
        return (
            <div className="panel" style={{ maxWidth: 640, marginBottom: '1.4rem' }}>
                <div className="spinner" style={{ margin: '0.5rem auto' }} />
            </div>
        );
    }

    return (
        <div className="panel" style={{ maxWidth: 640, marginBottom: '1.4rem' }} data-testid="restaurant-branding">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '0.6rem' }}>
                <Store size={18} color="var(--amber)" />
                <h2 className="headline" style={{ fontSize: '1.35rem', margin: 0 }}>Logo & cover meniu QR</h2>
            </div>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginTop: 0, lineHeight: 1.5 }}>
                Apare în meniul pe care clienții îl văd după scanarea QR-ului de la masă.
            </p>
            {!brandingAllowed && <PlanUpgradeNotice featureLabel="Logo & cover personalizat" compact />}

            {!loading && !uploadConfigured && (
                <div style={{
                    marginBottom: '1rem', padding: '0.8rem', borderRadius: 10,
                    background: 'rgba(0, 217, 255, 0.06)', border: '1px solid rgba(0, 217, 255, 0.25)',
                    fontSize: '0.85rem', color: 'var(--text-muted)',
                }}>
                    Upload-urile necesită Supabase configurat în backend (.env). Vezi SUPABASE_SETUP.md.
                </div>
            )}

            <div style={{ display: 'grid', gap: '1.2rem' }}>
                <ImageSlot
                    label="Imagine cover (fundal)"
                    hint="Poză largă a restaurantului — fundal în header-ul meniului client."
                    value={coverUrl}
                    uploading={coverBusy}
                    disabled={!uploadConfigured || !brandingAllowed}
                    error={coverError}
                    onUpload={uploadCover}
                    onRemove={removeCover}
                    testId="restaurant-cover"
                    aspect="wide"
                />
                <ImageSlot
                    label="Logo restaurant"
                    hint="Logo pătrat — afișat peste cover, lângă numele restaurantului."
                    value={logoUrl}
                    uploading={logoBusy}
                    disabled={!uploadConfigured || !brandingAllowed}
                    error={logoError}
                    onUpload={uploadLogo}
                    onRemove={removeLogo}
                    testId="restaurant-logo"
                    aspect="square"
                />
            </div>

            {(coverUrl || logoUrl) && (
                <div style={{ marginTop: '1rem', padding: '0.8rem', borderRadius: 10, background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, color: 'var(--text-muted)', fontSize: '0.78rem' }}>
                        <Image size={14} /> Previzualizare header client
                    </div>
                    <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid var(--border)' }}>
                        <div style={{
                            height: 100,
                            background: coverUrl
                                ? `linear-gradient(to bottom, rgba(0,0,0,0.15), rgba(10,10,10,0.92)), url(${coverUrl}) center/cover`
                                : 'linear-gradient(135deg, rgba(245,158,11,0.15), rgba(255,46,146,0.12))',
                        }} />
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0.75rem 1rem', marginTop: -24, position: 'relative' }}>
                            {logoUrl ? (
                                <img src={logoUrl} alt="" style={{ width: 48, height: 48, borderRadius: 12, objectFit: 'cover', border: '2px solid rgba(255,255,255,0.2)' }} />
                            ) : (
                                <div style={{ width: 48, height: 48, borderRadius: 12, background: 'var(--surface-2)', border: '1px dashed var(--border)' }} />
                            )}
                            <div>
                                <div className="kicker" style={{ marginBottom: 2 }}>Restaurant</div>
                                <div style={{ fontWeight: 600 }}>Masa 5</div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
